// Reusable animated d20 die component.
// Handles the spin → settle animation used by death saves and initiative rolls.
//
// Usage:
//   <d20-die spinning></d20-die>          — spinning with random faces (uncontrolled)
//   <d20-die face="14"></d20-die>         — settled on face 14 (static)
//   <d20-die .spinning="${true}" .finalFace="${12}" outcome="success"></d20-die>
//     — spins until finalFace is set, then settles on it with an outcome color
//
// Parent components control timing by toggling `spinning` and setting
// `finalFace` when the roll result is known.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

class D20Die extends BaseComponent {
  static properties = {
    // Controls the spin animation. When true, the die face cycles rapidly.
    // When false (or when finalFace is set), the die stops on finalFace.
    spinning: { type: Boolean, reflect: true },
    // The final rolled face. When set and spinning is false, the die is
    // displayed in the "settled" state showing this value.
    finalFace: { type: Number, attribute: 'final-face' },
    // Outcome color for the settled state. Accepted values:
    //   'neutral' | 'success' | 'failure' | 'critical-success' | 'critical-failure'
    outcome: { type: String },
    _face: { type: Number, state: true },
  };

  static styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      position: relative;
      width: 52px;
      height: 52px;
      user-select: none;
      color: var(--d20-color, #f87171);
    }

    svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }

    .face {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.85);
      border: 2px solid #fff;
      font-family: 'MedievalSharp', Georgia, serif;
      font-size: 18px;
      font-weight: 700;
      line-height: 1;
      color: #fff;
    }

    :host(.is-settled) .face {
      background: var(--d20-color, #22c55e);
    }

    :host(.is-spinning) {
      animation: die-spin 0.75s linear infinite;
    }

    :host(.is-settled) {
      animation: die-settle 0.6s ease-out 1;
    }

    @keyframes die-spin {
      0%   { transform: rotate(0deg) scale(1); }
      25%  { transform: rotate(90deg) scale(1.06); }
      50%  { transform: rotate(180deg) scale(0.94); }
      75%  { transform: rotate(270deg) scale(1.06); }
      100% { transform: rotate(360deg) scale(1); }
    }

    @keyframes die-settle {
      0%   { transform: scale(1.4); filter: brightness(1.6); }
      40%  { transform: scale(0.92); filter: brightness(1.3); }
      100% { transform: scale(1); filter: brightness(1); }
    }
  `;

  constructor() {
    super();
    this.spinning = false;
    /** @type {number|null} */
    this.finalFace = null;
    this.outcome = 'neutral';
    this._face = 1 + Math.floor(Math.random() * 20);
    /** @type {ReturnType<typeof setInterval>|null} */
    this._spinInterval = null;
  }

  /** @param {Map<string, unknown>} changedProps */
  updated(changedProps) {
    if (changedProps.has('spinning')) {
      if (this.spinning) {
        this._startSpin();
      } else {
        this._stopSpin();
      }
    }
    if (changedProps.has('finalFace') && this.finalFace != null) {
      this._face = this.finalFace;
      this._stopSpin();
    }
    if (changedProps.has('spinning') || changedProps.has('finalFace') || changedProps.has('outcome')) {
      this._updateVisualState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopSpin();
  }

  _startSpin() {
    if (this._spinInterval) return;
    this._spinInterval = setInterval(() => {
      this._face = 1 + Math.floor(Math.random() * 20);
    }, 160);
  }

  _stopSpin() {
    if (this._spinInterval) {
      clearInterval(this._spinInterval);
      this._spinInterval = null;
    }
  }

  _updateVisualState() {
    const settled = !this.spinning && this.finalFace != null;
    this.classList.toggle('is-spinning', this.spinning);
    this.classList.toggle('is-settled', settled);
    this.style.setProperty('--d20-color', this._colorForOutcome(settled));
  }

  /** @param {boolean} settled */
  _colorForOutcome(settled) {
    if (!settled) return '#f87171';
    switch (this.outcome) {
      case 'critical-success':
        return '#facc15';
      case 'success':
        return '#22c55e';
      case 'failure':
      case 'critical-failure':
        return '#ef4444';
      default:
        return '#3b82f6';
    }
  }

  render() {
    return html`
      <svg viewBox="0 0 100 100" fill="#fff" aria-hidden="true">
        <path d="M91.9,74.7C91.9,74.7,91.9,74.7,91.9,74.7c0.1-0.1,0.1-0.2,0.1-0.2c0,0,0-0.1,0-0.1c0,0,0-0.1,0-0.1c0,0,0-0.1,0-0.1c0,0,0-0.1,0-0.2c0,0,0-0.1,0-0.1c0-0.1,0-0.2,0-0.2V27.2c0,0,0,0,0-0.1c0,0,0-0.1,0-0.1c0,0,0-0.1,0-0.1c0,0,0,0,0-0.1c0,0,0,0,0-0.1c0,0,0-0.1,0-0.1c0,0,0-0.1,0-0.1c0,0,0,0,0,0c0-0.1-0.1-0.1-0.1-0.2c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0-0.1c0-0.1-0.1-0.1-0.1-0.1c0,0,0,0,0,0c0,0,0,0,0-0.1c0,0-0.1-0.1-0.1-0.1c0,0,0,0,0,0c0,0,0,0,0,0c0,0-0.1-0.1-0.2-0.1c0,0,0,0-0.1,0c0,0,0,0,0,0L50.9,2.3c-0.6-0.3-1.3-0.3-1.8,0L8.8,25.6c0,0,0,0,0,0c-0.1,0-0.1,0.1-0.2,0.1c0,0,0,0-0.1,0c0,0-0.1,0.1-0.1,0.1c0,0,0,0-0.1,0.1c0,0-0.1,0.1-0.1,0.1c0,0,0,0.1-0.1,0.1c0,0,0,0-0.1,0.1c0,0,0,0,0,0c0,0,0,0.1,0,0.1c0,0,0,0.1-0.1,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1c0,0,0,0,0,0.1v46.5c0,0,0,0,0,0v0c0,0.1,0,0.2,0,0.3c0,0,0,0.1,0,0.1c0,0.1,0.1,0.2,0.1,0.3c0,0,0,0,0,0c0,0,0,0,0,0c0,0.1,0.1,0.2,0.2,0.3c0,0,0,0,0,0.1C8.2,74.9,8.3,75,8.4,75c0,0,0,0,0,0c0.1,0.1,0.1,0.1,0.2,0.2c0,0,0,0,0.1,0c0,0,0,0,0.1,0l40.3,23.3c0,0,0,0,0,0c0,0,0.1,0,0.1,0.1c0,0,0.1,0,0.1,0c0,0,0.1,0,0.1,0c0,0,0.1,0,0.1,0c0,0,0.1,0,0.1,0c0,0,0,0,0.1,0c0.1,0,0.1,0,0.2,0c0.1,0,0.1,0,0.2,0c0,0,0,0,0.1,0c0.1,0,0.1,0,0.2,0c0,0,0.1,0,0.1,0c0,0,0.1,0,0.1,0c0,0,0.1,0,0.1,0c0,0,0.1,0,0.1-0.1c0,0,0,0,0,0l40.3-23.3c0,0,0,0,0,0c0,0,0,0,0.1,0c0,0,0,0,0.1,0c0,0,0.1,0,0.1-0.1c0,0,0.1-0.1,0.1-0.1c0,0,0.1,0,0.1-0.1c0,0,0.1-0.1,0.1-0.1c0,0,0-0.1,0.1-0.1C91.8,74.8,91.8,74.7,91.9,74.7z M11.9,37.6l11.2,27.8c0.1,0.3,0,0.7-0.4,0.9l-10.3,4.3c-0.4,0.2-0.9-0.1-0.9-0.6V37.7C11.5,37.5,11.8,37.4,11.9,37.6z M69.9,65.2H30.1c-0.5,0-0.8-0.6-0.6-1l19.9-34.5c0.3-0.4,0.9-0.4,1.1,0l19.9,34.5C70.7,64.7,70.4,65.2,69.9,65.2z M25.6,61.6L12.7,29.7c-0.2-0.4,0.1-0.9,0.6-0.9l32.2-1.6c0.5,0,0.9,0.5,0.6,1L26.7,61.7C26.5,62.2,25.8,62.2,25.6,61.6z M69.5,70l-19,23.4c-0.3,0.3-0.8,0.3-1,0L30.5,70c-0.3-0.4,0-1.1,0.5-1.1h38C69.5,68.9,69.8,69.6,69.5,70z M73.3,61.7L53.9,28.2c-0.3-0.5,0.1-1,0.6-1l32.2,1.6c0.5,0,0.7,0.5,0.6,0.9L74.4,61.6C74.2,62.2,73.5,62.2,73.3,61.7z M51.8,22.8V8.2c0-0.5,0.6-0.8,1-0.6L82,24.5c0.2,0.1,0.1,0.4-0.1,0.4l-29.5-1.5C52.1,23.4,51.8,23.1,51.8,22.8z M47.5,23.4l-29.5,1.5c-0.2,0-0.3-0.3-0.1-0.4L47.2,7.7c0.4-0.3,1,0.1,1,0.6v14.5C48.2,23.1,47.9,23.4,47.5,23.4z M25.5,69.7l16.1,19.9c0.1,0.2-0.1,0.4-0.3,0.3L15,74.7c-0.5-0.3-0.4-1,0.1-1.2l9.7-4C25,69.4,25.3,69.5,25.5,69.7z M75.2,69.5l9.7,4c0.5,0.2,0.6,0.9,0.1,1.2L58.6,89.9c-0.2,0.1-0.4-0.1-0.3-0.3l16.1-19.9C74.7,69.5,75,69.4,75.2,69.5z M87.6,70.6l-10.3-4.3c-0.3-0.1-0.5-0.5-0.4-0.9l11.2-27.8c0.1-0.2,0.4-0.1,0.4,0.1V70C88.5,70.5,88,70.8,87.6,70.6z" />
      </svg>
      <span class="face">${this._face}</span>
    `;
  }
}

customElements.define('d20-die', D20Die);
